/**
 * PerChildMaterialsSendBundle · iter303ak-fix27 — Accepted · T50 · Action 40
 * ────────────────────────────────────────────────────────────────────────
 * MOCKED per (child × supplier) send. Reads items from the previous
 * preview action, per-row editable Email + Send button + Send-all.
 * Auto-loads supplier email from `supplier_letterheads` collection.
 * Each send hits POST /appraisals/{child_id}/materials-order-pdf/{supplier_slug}/email
 * (MOCKED endpoint). Stamps `materials_order_sent_at.{child_id}.{supplier_slug}`
 * on the parent appraisal.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Mail, AlertCircle, Send, AlertTriangle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const nz = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PerChildMaterialsSendBundle({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const channelsKey = params.channels_source || 'materials_notify_channels';
  const channels = capturedData?.[channelsKey] || [];
  const stampFieldPrefix = params.stamp_field_prefix || 'materials_order_sent_at';

  const items = useMemo(() => {
    const preview = capturedData?.per_child_materials_order_preview || {};
    const raw = Array.isArray(preview?.items) ? preview.items : [];
    return raw.filter(i => i.include !== false);
  }, [capturedData]);

  const [rowState, setRowState] = useState(() => value?.row_state || {});
  const [sendState, setSendState] = useState(() => value?.send_state || {});
  const [busyKeys, setBusyKeys] = useState(() => new Set());
  const [globalErr, setGlobalErr] = useState('');
  const [enriched, setEnriched] = useState(false);

  // Enrich rows with supplier email from supplier_letterheads.
  const enrichRows = useCallback(async () => {
    if (!items.length || enriched) return;
    // Fetch letterheads once, index by slug.
    let letterheadIdx = {};
    try {
      const { data } = await api.get('/supplier-letterheads');
      const arr = Array.isArray(data) ? data : [];
      for (const lh of arr) {
        if (lh.slug) letterheadIdx[lh.slug] = lh;
        // Also match by name-slugged version as fallback
      }
    } catch { /* silent */ }

    setRowState(prev => {
      const next = { ...prev };
      for (const it of items) {
        const key = `${it.child_id}|${it.supplier_slug}`;
        if (next[key] && next[key].email !== undefined) continue;
        const lh = letterheadIdx[it.supplier_slug] || null;
        next[key] = {
          email: lh?.email || '',
          mobile: lh?.contact_number || lh?.phone || '',
          email_orig: lh?.email || '',
        };
      }
      return next;
    });
    setEnriched(true);
  }, [items, enriched]);

  useEffect(() => { enrichRows(); }, [enrichRows]);

  const isSent = (child_id, supplier_slug) => !!sendState[`${child_id}|${supplier_slug}`]?.sent_at;
  const errFor = (child_id, supplier_slug) => sendState[`${child_id}|${supplier_slug}`]?.error;

  const updateRow = (key, patch) => {
    setRowState(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };

  const isEmailValid = (e) => !!(e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim()));

  const sendOne = async (child_id, supplier_slug) => {
    const key = `${child_id}|${supplier_slug}`;
    const row = rowState[key] || {};
    if (!isEmailValid(row.email)) {
      setSendState(prev => ({ ...prev, [key]: { error: 'Enter a valid email above before sending.' } }));
      return;
    }
    setBusyKeys(prev => { const n = new Set(prev); n.add(key); return n; });
    setGlobalErr('');
    try {
      const r = await api.post(
        `/appraisals/${child_id}/materials-order-pdf/${encodeURIComponent(supplier_slug)}/email`,
        { to_email: (row.email || '').trim(), channels }
      );
      const nowIso = r?.data?.sent_at || new Date().toISOString();
      setSendState(prev => ({
        ...prev,
        [key]: { sent_at: nowIso, ok: true, response: r?.data || {}, to_email: (row.email || '').trim() },
      }));
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || 'Send failed';
      setSendState(prev => ({ ...prev, [key]: { error: errMsg } }));
      setGlobalErr(errMsg);
    } finally {
      setBusyKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const sendAll = async () => {
    for (const it of items) {
      if (isSent(it.child_id, it.supplier_slug)) continue;
      const key = `${it.child_id}|${it.supplier_slug}`;
      if (!isEmailValid((rowState[key] || {}).email)) continue;
      await sendOne(it.child_id, it.supplier_slug);
    }
  };

  useEffect(() => {
    if (onChange) onChange({ send_state: sendState, row_state: rowState });
  }, [sendState, rowState, onChange]);

  const allSent = items.length > 0 && items.every(i => isSent(i.child_id, i.supplier_slug));
  const sentCount = items.filter(i => isSent(i.child_id, i.supplier_slug)).length;

  return (
    <div className="space-y-4" data-testid="wf-per-child-materials-send-bundle">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Send Materials Orders to Suppliers</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Email each supplier their materials order. Edit each recipient email inline if needed.'}
              {channels?.length ? (
                <span className="ml-2 text-[11px] text-amber-700">
                  · Channel(s): <b>{Array.isArray(channels) ? channels.join(', ') : channels}</b>
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
          Sends a real email with the materials order PDF attached.
        </div>
      </div>

      {items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No supplier orders selected in the previous step. Go back and include at least one.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600">
              <b className="text-slate-800">{sentCount}</b> of <b className="text-slate-800">{items.length}</b> sent
            </div>
            <button
              type="button"
              onClick={sendAll}
              disabled={allSent || busyKeys.size > 0}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="wf-mat-send-all"
            >
              <Send size={12} /> {allSent ? 'All sent ✓' : (busyKeys.size > 0 ? 'Sending…' : 'Send all')}
            </button>
          </div>

          <div className="space-y-2">
            {items.map(it => {
              const key = `${it.child_id}|${it.supplier_slug}`;
              const row = rowState[key] || {};
              const sent = isSent(it.child_id, it.supplier_slug);
              const err = errFor(it.child_id, it.supplier_slug);
              const busy = busyKeys.has(key);
              const emailValid = isEmailValid(row.email);
              return (
                <div key={key} className={`p-3 border rounded-lg ${sent ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`} data-testid={`wf-mat-send-row-${it.child_id}-${it.supplier_slug}`}>
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800 truncate">{it.supplier_name}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>
                        {it.total != null && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            {nz(it.total)}
                          </span>
                        )}
                        {sent && (
                          <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                            <CheckCircle2 size={10} /> Sent
                          </span>
                        )}
                        {!row.email_orig && !sent && (
                          <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                            <AlertTriangle size={10} /> No email on file
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2 pl-3 border-l-2 border-slate-200">
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Email <span className="text-rose-500">*</span></label>
                      <input
                        type="email"
                        value={row.email || ''}
                        onChange={(e) => updateRow(key, { email: e.target.value })}
                        disabled={sent}
                        placeholder="supplier@email.com"
                        className={`w-full px-2 py-1 text-xs border rounded ${row.email && !emailValid ? 'border-rose-400 bg-rose-50' : 'border-slate-300'} disabled:opacity-60`}
                        data-testid={`wf-mat-send-email-${it.child_id}-${it.supplier_slug}`}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Contact number</label>
                      <input
                        type="tel"
                        value={row.mobile || ''}
                        onChange={(e) => updateRow(key, { mobile: e.target.value })}
                        disabled={sent}
                        placeholder="Supplier phone"
                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60"
                        data-testid={`wf-mat-send-mobile-${it.child_id}-${it.supplier_slug}`}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[10px] text-slate-500">
                      {sent && sendState[key]?.sent_at && <span className="text-emerald-700">✓ Sent {new Date(sendState[key].sent_at).toLocaleString()} to {sendState[key].to_email}</span>}
                      {err && <span className="text-rose-700">Error: {err}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => sendOne(it.child_id, it.supplier_slug)}
                      disabled={sent || busy || !emailValid}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
                      data-testid={`wf-mat-send-btn-${it.child_id}-${it.supplier_slug}`}
                      title={emailValid ? 'Send this materials order' : 'Enter a valid email first'}
                    >
                      <Send size={12} /> {sent ? 'Sent' : (busy ? 'Sending…' : 'Send')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {globalErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{globalErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-mat-send-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-mat-send-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!allSent}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-mat-send-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
